import useIsClient from "@karbonhq/marketing-shared-utils/hooks/useIsClient"
import { useMediaQuery } from "@karbonhq/marketing-shared-utils/hooks/useMediaQuery"
import PropTypes from "prop-types"
import React, { useCallback, useEffect, useRef, useState } from "react"
import styled, { css } from "styled-components"
import breakpoint from "styled-components-breakpoint"

import kpiJumbotronImageSrc from "../../images/kpi-jumbotron.jpg"
import arrowLeft from "../../images/tall-arrow-left.svg"
import arrowLeftGrey from "../../images/tall-arrow-left-grey.svg"
import arrowLeftHover from "../../images/tall-arrow-left-hover.svg"
import arrowRight from "../../images/tall-arrow-right.svg"
import arrowRightGrey from "../../images/tall-arrow-right-grey.svg"
import arrowRightHover from "../../images/tall-arrow-right-hover.svg"
import theme from "../../settings/theme"
import { ArrowButton } from "../atoms/ArrowButton"
import { H2, H5 } from "../atoms/Headings"
import { Link } from "../atoms/Link"

const LinkResolver = require("../../utils/linkResolver")

export const CAROUSEL_LG_BREAKPOINT = 1350

const JumbotronEl = styled.article`
  color: white;
  border-radius: 6px;
  height: 240px;
  background-color: ${props => (props.bgColour ? `${props.bgColour}` : "#aaa")};
  background-image: ${props =>
    props.bgImage === kpiJumbotronImageSrc
      ? `url("${props.bgImage}")`
      : props.bgImage
      ? `url("${props.bgImage?.file?.url}")`
      : null};
  background-position: ${props =>
    props.bg === "Icon" ? "calc(100% - 16px) calc(100% - 16px)" : `right`};
  background-repeat: no-repeat;
  background-size: ${props => (props.bg === "Icon" ? "auto 120px" : "cover")};

  ${breakpoint("md")(
    () => css`
      height: ${props => (props.width ? `${0.7 * props.width}px` : "auto")};
      width: 100%;
    `
  )}

  @media screen and (min-width: 880px) and (max-width: ${theme.breakpoints
    .lg}px) {
    background-size: ${props => (props.bg === "Icon" ? "auto 150px" : "cover")};
  }

  @media screen and (min-width: 1200px) and (max-width: 1350px) {
    background-size: ${props => (props.bg === "Icon" ? "auto 150px" : "cover")};
  }
`

const JumbotronContent = styled.div`
  padding: 24px 20px;
`

const Jumbotron = ({ template, width }) => {
  return (
    <Link to={LinkResolver(template.item?.internal?.type, template.item?.slug)}>
      <JumbotronEl
        width={width}
        bgColour={template.item.headerBackgroundColour}
        bgImage={template.item.headerImage}
        bg={template.item.backgroundDisplayMode}
      >
        <div
          css={css`
            width: 100%;
            height: 100%;
            position: relative;
          `}
        >
          <JumbotronContent>
            <H5
              css={css`
                letter-spacing: 0;
                margin-top: 0;
                margin-bottom: 4px;
                color: ${template.item.headerTextColour || "white"};
              `}
            >
              {template.supertitle}
            </H5>
            <H2
              mt={0}
              css={css`
                color: ${template.item.headerTextColour || "white"};
              `}
            >
              {template.item?.title}
            </H2>
          </JumbotronContent>
        </div>
      </JumbotronEl>
    </Link>
  )
}

export const JumbotronDisplay = ({ jumbotrons, first }) => {
  const isMobile = useMediaQuery("md", "max", theme.breakpoints)
  const [translate, setTranslate] = useState(0)
  const [divWidth, setDivWidth] = useState(356)
  // the total number of jumbotrons displayed
  const [numDisplayed, setNumDisplayed] = useState(2)
  // the last jumbotron displayed
  const [finalJumbotron, setFinalJumbotron] = useState(2)
  const isClient = useIsClient()
  const divRef = useRef()
  const [containerWidth, setContainerWidth] = useState(500)
  //KPI jumbotron is hardcoded, so we need to bump by one in first collection
  const jumbotronsLength = first ? jumbotrons.length + 1 : jumbotrons.length

  useEffect(() => {
    let numJumbotrons
    const setNumJumbotrons = () => {
      if (
        window.matchMedia(`(max-width: ${CAROUSEL_LG_BREAKPOINT}px)`).matches
      ) {
        numJumbotrons = 2
      } else {
        numJumbotrons = 3
      }
      // if the carousel is at the beginning, make sure final jumbotron updates when the screen size changes
      if (translate === 0) {
        setFinalJumbotron(numJumbotrons)
      }
      setNumDisplayed(numJumbotrons)
    }
    const setWidths = () => {
      const width = Math.floor(divRef.current.getBoundingClientRect().width)
      // calculate the size that each div should appear on the page
      // ( numDisplayed - 1 ) * 16 to account for grid gaps
      const div = Math.floor((width - 16 * (numJumbotrons - 1)) / numJumbotrons)
      setContainerWidth(width)
      setDivWidth(div)
    }
    const set = () => {
      setNumJumbotrons()
      setWidths()
    }
    set()
    window.addEventListener("resize", set)
    return () => {
      window.removeEventListener("resize", set)
    }
  }, [divRef, finalJumbotron, translate])
  const calculateTranslate = useCallback(
    final => {
      if (final > numDisplayed && !isMobile) {
        return (final - numDisplayed) * (divWidth + 16)
      }
      return 0
    },
    [divWidth, isMobile, numDisplayed]
  )

  useEffect(() => {
    setTranslate(calculateTranslate(finalJumbotron))
  }, [calculateTranslate, divWidth, finalJumbotron])

  const goForward = () => {
    if (finalJumbotron < jumbotronsLength) {
      let final
      if (finalJumbotron + numDisplayed > jumbotronsLength) {
        final = jumbotronsLength
      } else {
        final = finalJumbotron + numDisplayed
      }

      setFinalJumbotron(final)
      setTranslate(calculateTranslate(final))
    }
  }

  const goBackward = () => {
    if (translate > 0) {
      let final
      if (finalJumbotron - numDisplayed < 0) {
        final = 0
      } else {
        final = finalJumbotron - numDisplayed
      }

      setFinalJumbotron(final)
      setTranslate(calculateTranslate(final))
    }
  }

  // Do we have all the stuff we need to show a carousel?
  const isCarousel = jumbotronsLength > numDisplayed && !isMobile

  return (
    <>
      <div
        css={css`
          width: 100%;
          height: 1px;
          overflow: hidden;
        `}
        ref={divRef}
      />
      <div
        css={css`
          width: ${containerWidth}px;
          position: absolute;
          visibility: ${isClient ? `visible` : `hidden`};
        `}
      >
        {isCarousel && (
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              position: relative;
              margin-left: -30px;
              margin-right: -30px;
              top: 104px;

              ${breakpoint("md")(
                () =>
                  css`
                    top: ${(0.7 * divWidth - 32) * 0.5}px;
                  `
              )}
            `}
          >
            <ArrowButton
              css={css`
                background-image: url(${translate > 0
                  ? arrowLeft
                  : arrowLeftGrey});

                ${translate > 0 &&
                css`
                  &:hover {
                    background-image: url(${arrowLeftHover});
                  }
                `}
              `}
              onClick={goBackward}
            />

            <ArrowButton
              css={css`
                background-image: url(${finalJumbotron < jumbotronsLength
                  ? arrowRight
                  : arrowRightGrey});

                ${finalJumbotron < jumbotronsLength &&
                css`
                  &:hover {
                    background-image: url(${arrowRightHover});
                  }
                `}
              `}
              onClick={goForward}
            />
          </div>
        )}
      </div>

      <div
        css={css`
          overflow-x: auto;
          width: ${containerWidth}px;
          visibility: ${isClient ? `visible` : `hidden`};

          ${breakpoint("md")(
            () =>
              css`
                overflow: hidden;
              `
          )};
        `}
      >
        <div
          // Set with a style so we dont end up re rendering the component
          // classname and forcing major repaints
          style={{
            gridTemplateColumns: `repeat(${jumbotronsLength}, ${
              isMobile ? 343 : divWidth
            }px)`,
            transform: `translateX(-${translate}px)`,
          }}
          css={css`
            display: grid;
            grid-column-gap: 8px;
            transition: transform 0.2s ease;

            ${breakpoint("md")(
              css`
                grid-column-gap: 16px;
              `
            )}
          `}
        >
          {first && (
            <Link to={"/kpi-dashboards"}>
              <JumbotronEl
                width={"100%"}
                bgColour={"#000"}
                bgImage={kpiJumbotronImageSrc}
                style={{ height: `100%` }}
              >
                <div
                  css={css`
                    width: 100%;
                    height: 100%;
                    position: relative;
                  `}
                >
                  <JumbotronContent>
                    <H5
                      css={css`
                        letter-spacing: 0;
                        margin-top: 0;
                        margin-bottom: 4px;
                        color: #000;
                      `}
                    >
                      Featured Collection
                    </H5>
                    <H2
                      mt={0}
                      css={css`
                        color: #000;
                      `}
                    >
                      Karbon Practice Intelligence Dashboards
                    </H2>
                  </JumbotronContent>
                </div>
              </JumbotronEl>
            </Link>
          )}
          {jumbotrons.map((template, i) => (
            <Jumbotron
              first={i === 0}
              template={template}
              width={divWidth}
              key={template.item.title}
            />
          ))}
        </div>
      </div>
    </>
  )
}

JumbotronDisplay.propTypes = {
  jumbotrons: PropTypes.arrayOf(PropTypes.object).isRequired,
}
